import React, { useContext, useState, useEffect } from "react";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    DialogActions,
    Button,
    Input,
    RadioGroup,
    Radio,
    Field,
    Spinner,
    Combobox,
    Option,
    OptionOnSelectData,
} from "@fluentui/react-components";
import { ApiContext } from "../providers/ApiProvider";
import { IProjectResults, ITimeEntry, IUser } from "../types/types";
import { IDropdownElement } from "./WeekTable";
import moment from "moment";

interface IBulkFillHoursDialogProps {
    token: string;
    user: IUser;
    projectsData: IProjectResults[] | undefined;
    onSuccess: (updatedFieldNames?: string[]) => void;
}

type FillMode = "budget" | "unlimited";

export default function BulkFillHoursDialog(props: IBulkFillHoursDialogProps) {
    const { submitTimeEntry, updateTimeEntry, getTimeEntriesBetween } = useContext(ApiContext);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState<string>("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [hoursPerDay, setHoursPerDay] = useState<string>("7.5");
    const [fillMode, setFillMode] = useState<FillMode>("unlimited");
    const [totalHours, setTotalHours] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [preview, setPreview] = useState<{ date: string; hours: number }[]>([]);
    const [error, setError] = useState<string>("");
    const [searchQuery, setSearchQuery] = useState<string>("");

    // Calculate weekdays in date range
    const getWeekdaysInRange = (start: Date, end: Date): Date[] => {
        const weekdays: Date[] = [];
        const startMoment = moment(start).startOf("day");
        const endMoment = moment(end).endOf("day");
        const current = startMoment.clone();

        while (current.isSameOrBefore(endMoment, "day")) {
            const dayOfWeek = current.day();
            // Monday = 1, Friday = 5
            if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                weekdays.push(current.toDate());
            }
            current.add(1, "day");
        }
        return weekdays;
    };

    // Calculate preview when inputs change
    useEffect(() => {
        if (!startDate || !endDate || !hoursPerDay || !selectedProject) {
            setPreview([]);
            return;
        }

        try {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (start > end) {
                setError("Start date must be before end date");
                setPreview([]);
                return;
            }

            // Enforce maximum range of 3 months between start and end dates
            const startMoment = moment(start).startOf("day");
            const endMoment = moment(end).endOf("day");
            const maxEndMoment = startMoment.clone().add(3, "months").endOf("day");
            if (endMoment.isAfter(maxEndMoment)) {
                setError("Date range cannot exceed 3 months");
                setPreview([]);
                return;
            }

            const weekdays = getWeekdaysInRange(start, end);
            const hoursPerDayNum = parseFloat(hoursPerDay);

            if (isNaN(hoursPerDayNum) || hoursPerDayNum <= 0 || hoursPerDayNum > 24) {
                setError("Hours per day must be between 0 and 24");
                setPreview([]);
                return;
            }

            setError("");

            if (fillMode === "budget") {
                const totalHoursNum = parseFloat(totalHours);
                if (isNaN(totalHoursNum) || totalHoursNum <= 0) {
                    setError("Total hours must be a positive number");
                    setPreview([]);
                    return;
                }

                // Distribute hours across weekdays
                const previewData: { date: string; hours: number }[] = [];
                let remainingHours = totalHoursNum;

                for (const day of weekdays) {
                    const hoursForDay = Math.min(hoursPerDayNum, remainingHours);
                    if (hoursForDay > 0) {
                        // Format date in local timezone (YYYY-MM-DD)
                        const year = day.getFullYear();
                        const month = String(day.getMonth() + 1).padStart(2, "0");
                        const date = String(day.getDate()).padStart(2, "0");
                        previewData.push({
                            date: `${year}-${month}-${date}`,
                            hours: hoursForDay,
                        });
                        remainingHours -= hoursForDay;
                    }
                }

                setPreview(previewData);
            } else {
                // Unlimited mode - fill all weekdays with same hours
                const previewData = weekdays.map((day) => {
                    // Format date in local timezone (YYYY-MM-DD)
                    const year = day.getFullYear();
                    const month = String(day.getMonth() + 1).padStart(2, "0");
                    const date = String(day.getDate()).padStart(2, "0");
                    return {
                        date: `${year}-${month}-${date}`,
                        hours: hoursPerDayNum,
                    };
                });
                setPreview(previewData);
            }
        } catch (err) {
            setError("Invalid date format");
            setPreview([]);
        }
    }, [startDate, endDate, hoursPerDay, fillMode, totalHours, selectedProject]);

    const handleSubmit = async () => {
        if (!selectedProject || !startDate || !endDate || !hoursPerDay || preview.length === 0) {
            setError("Please fill in all required fields");
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            // Parse dates using moment UTC (matching system convention)
            const startTimestamp = moment(startDate).utc(true).startOf("day").valueOf();
            const endTimestamp = moment(endDate).utc(true).endOf("day").valueOf();

            // Fetch existing time entries for the date range
            const existingEntries = await getTimeEntriesBetween(props.token, {
                from: startTimestamp,
                to: endTimestamp,
            });

            // Create a map of existing entries by project_id and date
            const existingEntriesMap = new Map<string, ITimeEntry>();
            if (existingEntries && existingEntries.timeEntries) {
                existingEntries.timeEntries.forEach((entry: ITimeEntry) => {
                    if (entry.project_id === selectedProject) {
                        // Format date in local timezone (YYYY-MM-DD)
                        const entryDate = new Date(entry.from);
                        const year = entryDate.getFullYear();
                        const month = String(entryDate.getMonth() + 1).padStart(2, "0");
                        const date = String(entryDate.getDate()).padStart(2, "0");
                        const dateKey = `${year}-${month}-${date}`;
                        existingEntriesMap.set(dateKey, entry);
                    }
                });
            }

            const today = new Date();
            const promises: Promise<void>[] = [];
            const updatedFieldNames: string[] = []; // Track which fields were updated

            // Process each day in preview
            for (const item of preview) {
                // Parse date using moment UTC (matching system convention)
                const fromTimestamp = moment(item.date).utc(true).startOf("day").valueOf();
                const toTimestamp = fromTimestamp + item.hours * 3600 * 1000;

                // Create field name in format: projectID_timestamp
                const fieldName = `${selectedProject}_${fromTimestamp}`;
                updatedFieldNames.push(fieldName);

                const existingEntry = existingEntriesMap.get(item.date);

                if (existingEntry) {
                    // Update existing entry
                    const updatedEntry: ITimeEntry = {
                        ...existingEntry,
                        from: fromTimestamp,
                        to: toTimestamp,
                        modified: today.valueOf(),
                    };
                    promises.push(updateTimeEntry(props.token, updatedEntry, existingEntry.id!));
                } else {
                    // Create new entry
                    const newEntry: ITimeEntry = {
                        project_id: selectedProject,
                        created_by: props.user.id,
                        created: today.valueOf(),
                        modified: today.valueOf(),
                        description: "",
                        from: fromTimestamp,
                        to: toTimestamp,
                        approved: false,
                    };
                    promises.push(submitTimeEntry(props.token, newEntry));
                }
            }

            await Promise.all(promises);

            // Clear local change tracking for updated fields so merge logic uses server values
            // This needs to be passed via onSuccess callback
            setIsOpen(false);
            props.onSuccess(updatedFieldNames);
        } catch (err: any) {
            console.error("Error filling hours:", err);
            setError(err.message || "Failed to fill hours. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        setSelectedProject("");
        setStartDate("");
        setEndDate("");
        setHoursPerDay("7.5");
        setFillMode("unlimited");
        setTotalHours("");
        setPreview([]);
        setError("");
        setSearchQuery("");
    };

    const selectedProjectName = props.projectsData?.find((p) => p.id === selectedProject)?.project_name || "";

    // Filter projects based on search query
    const filteredProjects =
        props.projectsData?.filter((project) => {
            if (!searchQuery) return true;
            const displayText = `${project.company_name} - ${project.project_name}`.toLowerCase();
            return displayText.includes(searchQuery.toLowerCase());
        }) || [];

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchQuery(value);
        // Clear selection if user is typing (not selecting from dropdown)
        if (
            value &&
            !props.projectsData?.find((p) => {
                const displayText = `${p.company_name} - ${p.project_name}`;
                return displayText === value;
            })
        ) {
            setSelectedProject("");
        }
    };

    const handleOptionSelect = (_ev: any, data: OptionOnSelectData) => {
        if (data.optionValue) {
            setSelectedProject(data.optionValue);
            const project = props.projectsData?.find((p) => p.id === data.optionValue);
            if (project) {
                setSearchQuery(`${project.company_name} - ${project.project_name}`);
            }
        }
    };

    // Handle ESC key to close dialog
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === "Escape" && isOpen) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscKey);
        }

        return () => {
            document.removeEventListener("keydown", handleEscKey);
        };
    }, [isOpen]);

    return (
        <Dialog open={isOpen}>
            <DialogTrigger disableButtonEnhancement>
                <Button appearance="primary" onClick={() => setIsOpen(true)} className="whitespace-nowrap">
                    Bulk Fill Hours
                </Button>
            </DialogTrigger>
            <DialogSurface className="min-w-[90%] md:min-w-[600px] max-w-[800px]">
                <DialogBody>
                    <DialogTitle>Bulk Fill Hours</DialogTitle>
                    <DialogContent>
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center py-8 gap-4">
                                <Spinner size="large" />
                                <p>Filling hours...</p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4 py-4">
                                {/* Project Selection */}
                                <Field label="Select Project" required>
                                    <Combobox
                                        id="bulk-fill-project-select"
                                        placeholder="Search and select project..."
                                        value={
                                            selectedProject
                                                ? (() => {
                                                      const project = props.projectsData?.find((p) => p.id === selectedProject);
                                                      return project ? `${project.company_name} - ${project.project_name}` : "";
                                                  })()
                                                : searchQuery
                                        }
                                        onChange={handleInputChange}
                                        onOptionSelect={handleOptionSelect}
                                    >
                                        {filteredProjects.map((project) => {
                                            const displayText = `${project.company_name} - ${project.project_name}`;
                                            return (
                                                <Option key={project.id} value={project.id} text={displayText}>
                                                    {displayText}
                                                </Option>
                                            );
                                        })}
                                    </Combobox>
                                </Field>

                                {/* Date Range */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Field label="Start Date" required className="flex-1">
                                        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                    </Field>
                                    <Field label="End Date" required className="flex-1">
                                        <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                    </Field>
                                </div>

                                {/* Fill Mode */}
                                <Field label="Fill Mode">
                                    <RadioGroup value={fillMode} onChange={(_, data) => setFillMode(data.value as FillMode)}>
                                        <Radio value="unlimited" label="Unlimited (fill all weekdays with same hours)" />
                                        <Radio value="budget" label="Budget (distribute total hours across weekdays)" />
                                    </RadioGroup>
                                </Field>

                                {/* Hours Per Day */}
                                <Field label="Hours Per Day" required>
                                    <Input
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        max="24"
                                        value={hoursPerDay}
                                        onChange={(e) => setHoursPerDay(e.target.value)}
                                        placeholder="e.g., 7.5"
                                    />
                                </Field>

                                {/* Total Hours (Budget Mode) */}
                                {fillMode === "budget" && (
                                    <Field label="Total Hours Available" required>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            value={totalHours}
                                            onChange={(e) => setTotalHours(e.target.value)}
                                            placeholder="e.g., 100"
                                        />
                                    </Field>
                                )}

                                {/* Error Message */}
                                {error && (
                                    <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-400 text-sm">
                                        {error}
                                    </div>
                                )}

                                {/* Preview */}
                                {preview.length > 0 && (
                                    <div className="mt-4">
                                        <Field label={`Preview (${preview.length} weekdays will be filled)`}>
                                            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-2">
                                                <div className="grid grid-cols-2 gap-2 text-sm">
                                                    <div className="font-semibold">Date</div>
                                                    <div className="font-semibold">Hours</div>
                                                    {preview.map((item, idx) => (
                                                        <React.Fragment key={idx}>
                                                            <div>{item.date}</div>
                                                            <div>{item.hours.toFixed(1)}</div>
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                                {fillMode === "budget" && (
                                                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-sm">
                                                        <strong>Total: </strong>
                                                        {preview.reduce((sum, item) => sum + item.hours, 0).toFixed(1)} hours
                                                        {parseFloat(totalHours) > 0 && (
                                                            <span className="ml-2">
                                                                (Remaining:{" "}
                                                                {(
                                                                    parseFloat(totalHours) -
                                                                    preview.reduce((sum, item) => sum + item.hours, 0)
                                                                ).toFixed(1)}{" "}
                                                                hours)
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </Field>
                                    </div>
                                )}
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary" onClick={handleReset} disabled={isLoading}>
                                Reset
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary" onClick={() => setIsOpen(false)} disabled={isLoading}>
                                Cancel
                            </Button>
                        </DialogTrigger>
                        <Button
                            appearance="primary"
                            onClick={handleSubmit}
                            disabled={isLoading || preview.length === 0 || !selectedProject}
                        >
                            Fill Hours
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}
